
import { AlignLeft, DollarSign, Download, Globe, Image as ImageIcon, ListFilter, Loader2, Smartphone, Tag } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient'; 
import { styles } from '../../styles'; 

export function FeedPublisher() {
  const [videoUrl, setVideoUrl] = useState(''); 
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [thumbUrl, setThumbUrl] = useState(''); 
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('shorts'); // Padrão já como shorts
  const [productLink, setProductLink] = useState('');
  const [downloadLink, setDownloadLink] = useState('');
  const [tags, setTags] = useState('');
  const [plataforma, setPlataforma] = useState('YouTube Shorts');

  const [loading, setLoading] = useState(false);
  const [previewThumb, setPreviewThumb] = useState('');

  // 🪄 IA DO GEMINI
  const handleGeminiMagic = async () => {
    if (!title) return alert("Digite o assunto primeiro!");
    setLoading(true);
    const API_KEY = process.env.REACT_APP_GEMINI_API_KEY;
    
    try {
      const URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-1.5-pro:generateContent?key=${API_KEY}`;
      const response = await fetch(URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contents: [{ parts: [{ text: `Gere um JSON para o assunto "${title}" focado em Shorts verticais. Categorias: shorts, games, curiosidades. Formato: {"novoTitulo": "", "sinopse": "", "tags": "", "categoria": ""}` }] }]
        })
      });

      const result = await response.json();
      if (result.error) throw new Error(result.error.message);

      const text = result.candidates[0].content.parts[0].text;
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const data = JSON.parse(jsonMatch[0]);
        setTitle(data.novoTitulo);
        setDescription(data.sinopse);
        setTags(data.tags);
        setCategory(data.categoria || "shorts");
      }
    } catch (err: any) {
      alert("IA diz: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // 🖼️ LÓGICA DE PRÉVIA PARA SHORTS (Suporte a /shorts/ do YouTube)
  useEffect(() => {
    const extrairIdUniversal = (url: string) => {
      if (!url) return null;
      const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/;
      const match = url.match(ytRegex);
      if (match && match[1]) return match[1];
      try {
        if (url.includes('youtube.com/watch')) {
          const urlObj = new URL(url);
          const videoId = urlObj.searchParams.get('v');
          if (videoId && videoId.length === 11) return videoId;
        }
      } catch (e) {}
      return null;
    };

    if (thumbFile) {
      const url = URL.createObjectURL(thumbFile);
      setPreviewThumb(url);
      return () => URL.revokeObjectURL(url);
    } else if (thumbUrl) {
      setPreviewThumb(thumbUrl);
    } else {
      const idFinal = extrairIdUniversal(videoUrl);
      if (idFinal) setPreviewThumb(`https://img.youtube.com/vi/${idFinal}/maxresdefault.jpg`);
      else setPreviewThumb('');
    }
  }, [thumbFile, thumbUrl, videoUrl]);

  // 🚀 LÓGICA DE PUBLICAÇÃO DO SHORT
  const handlePublishFeed = async () => {
    if (!title) return alert("Digite pelo menos o Título!");
    if (!videoUrl) return alert("Insira o link do YouTube Shorts!");
    setLoading(true);

    const extrairIdYoutube = (url: string) => {
      if (!url) return null;
      const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/;
      const match = url.match(ytRegex);
      if (match && match[1]) return match[1];
      return null;
    };

    try {
      let finalThumbUrl = thumbUrl || previewThumb;
      const embedId = extrairIdYoutube(videoUrl);

      if (thumbFile) {
        const fileExt = thumbFile.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError } = await supabase.storage.from('videos_feed').upload(fileName, thumbFile);
        if (!uploadError) {
          const { data } = supabase.storage.from('videos_feed').getPublicUrl(fileName);
          finalThumbUrl = data.publicUrl;
        }
      }

      // 👈 SALVANDO COMO 'shorts' PARA O SITE RECONHECER NA ABA SHORTS
      const { error: dbError } = await supabase.from('videos').insert([{
        title, 
        description, 
        url: videoUrl, 
        thumbnail: finalThumbUrl,
        embed_id: embedId,
        category, 
        product_link: productLink, 
        download_link: downloadLink,
        plataforma: plataforma,
        post_type: 'feed_web', // 👈 MARCADO COMO SHORTS
        tags: tags.toLowerCase(),
        likes_count: 0, 
        comments_count: 0, 
        views_count: 0,
        is_ad: false,
        is_short: true // 👈 MARCADO COMO TRUE
      }]);

      if (dbError) throw dbError;
      
      alert(`✅ SUCESSO! Short publicado com sucesso!`);
      setVideoUrl(''); setThumbUrl(''); setThumbFile(null); setTitle(''); 
      setDescription(''); setDownloadLink(''); setProductLink(''); setTags('');
    } catch (err: any) {
      alert("Erro: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const extrairIdYoutubePlayer = (url: string) => {
    if (!url) return null;
    const ytRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/shorts\/)([^"&?\/\s]{11})/;
    const match = url.match(ytRegex);
    return (match && match[1]) ? match[1] : null;
  };

  const youtubeId = extrairIdYoutubePlayer(videoUrl);

  return (
    <div>
      <style>{`
        .feed-publisher-layout {
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 30px;
          width: 100%;
          box-sizing: border-box;
        }
        .feed-form-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 20px;
        }
        @media (max-width: 768px) {
          .feed-publisher-layout {
            display: flex;
            flex-direction: column-reverse;
            gap: 15px;
          }
          .feed-form-inner {
            display: flex;
            flex-direction: column;
            gap: 15px;
          }
        }
      `}</style>

      <div className="feed-publisher-layout">
        <div style={styles.formCard}>
          <h2 style={{ color: '#E50914', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10, fontSize: '18px' }}>
            <Smartphone size={22} /> Publicar YouTube Short (Vertical 9:16)
          </h2>

          <div className="feed-form-inner">
            {/* COLUNA ESQUERDA */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Link do YouTube Shorts</label>
                <input style={styles.input} value={videoUrl} onChange={e => setVideoUrl(e.target.value)} placeholder="https://youtube.com/shorts/..." />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><ImageIcon size={14}/> Capa Opcional (Upload ou Link)</label>
                <input type="file" accept="image/*" onChange={e => setThumbFile(e.target.files?.[0] || null)} style={{...styles.input, marginBottom: 5}} />
                <input style={styles.input} value={thumbUrl} onChange={e => setThumbUrl(e.target.value)} placeholder="Ou cole o link..." />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><Tag size={14}/> Tags de Busca</label>
                <input style={styles.input} value={tags} onChange={e => setTags(e.target.value)} placeholder="shorts, games, viral..." />
              </div>
              
              <div style={styles.inputGroup}>
                <label style={styles.label}><Globe size={16} color="#E50914"/> Plataforma</label>
                <input placeholder="Ex: YouTube Shorts" style={styles.input} value={plataforma} onChange={(e) => setPlataforma(e.target.value)} />
              </div>
            </div>

            {/* COLUNA DIREITA */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              <div style={styles.inputGroup}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={styles.label}>Título</label>
                  <button type="button" onClick={handleGeminiMagic} style={{ backgroundColor: '#6200ee', color: '#fff', border: 'none', borderRadius: 4, padding: '2px 8px', fontSize: 10, cursor: 'pointer' }}>
                    🪄 IA GEMINI
                  </button>
                </div>
                <input style={styles.input} value={title} onChange={e => setTitle(e.target.value)} placeholder="Título envolvente..." />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><ListFilter size={14}/> Categoria</label>
                <select style={styles.input} value={category} onChange={e => setCategory(e.target.value)}>
                  {/* Opção padrão para Shorts */}
                  <option value="shorts">📱 Shorts Geral</option>
                  <option value="oculto">👁️ Invisível (Só Pesquisa)</option>
                  
                  <optgroup label="🔥 DESTAQUES E EXPLORE">
                    <option value="curiosidades">💡 Curiosidades</option>
                    <option value="celebridade">⭐ Celebridades</option>
                    <option value="top10">🔝 Top 10</option>
                    <option value="top5">⚡ Top 5</option>
                    <option value="novidades">🆕 Novidades</option>
                    <option value="viral">🔥 Vídeos Virais</option>
                  </optgroup>
                  
                  <optgroup label="🚀 NOVIDADES DA COMUNIDADE">
                    <option value="ao_vivo">🔴 Ao Vivo & Resenha</option>
                    <option value="votacao">🏆 Votação da Galera</option>
                    <option value="documentario">🍿 Mistérios & Documentários</option>
                    <option value="canais">📺 Canais dos Crias</option>
                  </optgroup>

                  <optgroup label="✨ DIVERSÃO">
                    <option value="tecnologia:ia">🤖 I.A(I.A)</option>
                    <option value="podcast">🎙️ Podcast</option>
                    <option value="desenho">🎨 Desenhos</option>
                    <option value="nostalgia">🎞️ Nostalgia</option>
                    <option value="outros">Outros</option>
                  </optgroup>

                  <optgroup label="🎬 CINEMA & TV">
                    <option value="filmes:acao">Ação</option>
                    <option value="filmes:aventura">Aventura</option>
                    <option value="filmes:terror">Terror & Suspense</option>
                    <option value="filmes:ficcao">Ficção Científica</option>
                    <option value="filmes:nacional">🇧🇷 Filmes Brasileiros</option>
                    <option value="dorama">🇰🇷 Doramas</option>
                    <option value="filmes:comedia">Comédia</option>
                    <option value="filmes:trailer_cena">🎬 Trailers & Cenas</option>
                    <option value="serie">Série</option>
                    <option value="novela">Novela</option>
                  </optgroup>

                  <optgroup label="⚽ VIDA & ESPORTE">
                    <option value="esporte">Esporte</option>
                  </optgroup>

                  <optgroup label="🎮 MUNDO GAME">
                    <option value="games">Geral / Todos</option>
                    <option value="games:acao">Ação</option>
                    <option value="games:aventura">Aventura</option>
                    <option value="games:terror">Terror</option>
                    <option value="games:rpg">RPG</option>
                    <option value="games:esporte">Esporte</option>
                    <option value="games:playstation">PlayStation</option>
                    <option value="games:xbox">Xbox</option>
                    <option value="games:nintendo">Nintendo</option>
                    <option value="games:pc">PC Gamer</option>
                  </optgroup>

                  <optgroup label="🔥 DESTAQUES">
                    <option value="lancamento">Lançamento</option>
                    <option value="novidades:mais-visto">Mais Visto</option>
                  </optgroup>
                </select>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><Download size={14}/> Link de Download (Opcional)</label>
                <input style={styles.input} value={downloadLink} onChange={e => setDownloadLink(e.target.value)} placeholder="Link externo..." />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}><DollarSign size={14}/> Link de Afiliado (Opcional)</label>
                <input style={styles.input} value={productLink} onChange={e => setProductLink(e.target.value)} placeholder="Shopee, Hotmart..." />
              </div>
            </div>
          </div>

          <div style={{ ...styles.inputGroup, marginTop: 15 }}>
            <label style={styles.label}><AlignLeft size={14}/> Descrição / Legenda</label>
            <textarea style={{...styles.input, height: 60, resize: 'none'}} value={description} onChange={e => setDescription(e.target.value)} />
          </div>

          <button type="button" onClick={handlePublishFeed} disabled={loading} style={{...styles.btnPublish, marginTop: 20, backgroundColor: '#E50914', color: '#fff', width: '100%'}}>
            {loading ? <Loader2 className="animate-spin" /> : 'PUBLICAR SHORT NO SITE'}
          </button>
        </div>

        {/* 📱 PRÉVIA VERTICAL 9:16 (ESTILO CELULAR) */}
        <aside style={{ backgroundColor: '#111', padding: 20, borderRadius: 20, border: '1px solid #222', textAlign: 'center', height: 'fit-content' }}>
          <h3 style={{ color: '#fff', marginBottom: 15, fontSize: 16 }}>PRÉVIA: SHORTS (9:16)</h3>
          <div style={{ width: '240px', aspectRatio: '9/16', backgroundColor: '#000', margin: '0 auto', borderRadius: 16, border: '3px solid #333', overflow: 'hidden', position: 'relative' }}>
            {youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=1`}
                title="Player Preview"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            ) : previewThumb ? (
              <img src={previewThumb} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="Preview" />
            ) : (
              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Smartphone color="#444" size={50} />
              </div>
            )}
            <div style={{ position: 'absolute', bottom: 0, width: '100%', padding: 10, background: 'linear-gradient(transparent, #000)', boxSizing: 'border-box', pointerEvents: 'none' }}>
              <p style={{ color: '#fff', fontSize: 11, textAlign: 'left', fontWeight: 'bold', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title || 'Título do Short'}</p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}